/**
 * THE `{{variable}}` resolver every request kind sends with — HTTP, SSE, gRPC
 * and WebSocket (card "gRPC / WebSocket requests never substitute {{variables}}",
 * 04.10.2026).
 *
 * Before this module the HTTP builder and the SSE builder each wired
 * `createVariableResolver` to the scopes by hand, and the gRPC and WebSocket
 * builders did not substitute at all: `{{base}}/feed` could not even connect,
 * and gRPC metadata went out with literal braces. One function now says what a
 * name resolves to, in one order, for all four:
 *
 *   1. script-local variables (`pm.variables.set` in an HTTP pre-request
 *      script — only the HTTP Send path has them, passed as `localVars`);
 *   2. this session's `boo.environment.set()` writes (utils/scriptEnvOverlay);
 *   3. the stored scopes in THE order (utils/variablePrecedence): folder chain
 *      (nearest first) -> environment -> collection -> workspace -> global;
 *   4. dynamic computed variables (`{{$timestamp}}`, `{{$randomUUID}}`, …).
 *
 * An unknown name stays as literal `{{name}}` text, exactly as HTTP has always
 * sent it — no warning on HTTP, so none here either.
 *
 * Secrets: a `secret: true` variable's value already lives in JS (it is masked
 * in the UI, not withheld from Send), and HTTP substitutes it in JS before the
 * request reaches Rust. The other kinds do the same. What they must NOT do is
 * write the substituted secret into a log the user (or a screen recording)
 * sees — `createLogSafeResolver` is the variant for that: a secret stays
 * `{{name}}`.
 */
import { createVariableResolver, extractVariableDependencies } from './resolveVariables';
import { resolveDynamicVariable } from './dynamicVariables';
import { lookupScopedVariable } from './variablePrecedence';
import { getScriptEnvOverlayValue } from './scriptEnvOverlay';

/** The stored scopes keyed for utils/variablePrecedence, from a builder's props. */
export function storedScopesFrom({
  folderVariables,
  collectionVariables,
  selectedEnvironment,
  workspaceVariables,
  globalVariables,
} = {}) {
  return {
    folder: folderVariables,
    collection: collectionVariables,
    environment: selectedEnvironment?.variables,
    workspace: workspaceVariables,
    global: globalVariables,
  };
}

const hasOwn = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);

/** One name -> its raw (unexpanded) value, or null. Layers 1-3 above. */
function rawLookup(scopes, localVars) {
  return (variableName) => {
    const name = String(variableName).trim();
    const local = typeof localVars === 'function' ? localVars() : localVars;
    if (hasOwn(local, name)) return local[name];
    const overlay = getScriptEnvOverlayValue(name);
    if (overlay !== undefined) return overlay;
    const hit = lookupScopedVariable(name, scopes);
    return hit ? hit.variable.value : null;
  };
}

/** Whether the stored row that wins for `name` is a secret. */
function isSecretName(name, scopes) {
  return lookupScopedVariable(name, scopes)?.variable?.secret === true;
}

/**
 * @param {object} scopes  `{folder, collection, environment, workspace, global}` row lists
 * @param {{localVars?: object|(() => object)}} [options]
 *   Script-local variables, or a getter (read at every call, so a ref works).
 * @returns {(text: any) => any} substitute — non-strings pass through.
 */
export function createScopedVariableResolver(scopes = {}, { localVars } = {}) {
  return createVariableResolver(rawLookup(scopes, localVars), { fallback: resolveDynamicVariable });
}

/**
 * The same resolution for DISPLAY: a secret variable is left as `{{name}}`, so
 * a log row shows which secret went out without showing its value. Dynamic
 * variables are computed afresh here, so use it only when `involvesSecret` says
 * the text needs it (see `logSafeText`).
 */
export function createLogSafeResolver(scopes = {}, { localVars } = {}) {
  const lookup = rawLookup(scopes, localVars);
  return createVariableResolver(
    (name) => (isSecretName(String(name).trim(), scopes) ? null : lookup(name)),
    { fallback: resolveDynamicVariable }
  );
}

/** True when resolving `text` reads a secret variable — directly or through another variable's value. */
export function involvesSecret(text, scopes = {}, { localVars } = {}) {
  if (typeof text !== 'string' || !text.includes('{{')) return false;
  const lookup = rawLookup(scopes, localVars);
  return extractVariableDependencies(text, lookup).some(({ name }) => isSecretName(name, scopes));
}

/**
 * What a log may show for a value that was sent as `sent`: the sent value
 * itself, unless a secret took part — then the template resolved with secrets
 * kept as `{{name}}`.
 */
export function logSafeText(template, sent, scopes = {}, options = {}) {
  if (!involvesSecret(template, scopes, options)) return sent;
  return createLogSafeResolver(scopes, options)(template);
}
